import { motion } from "framer-motion";
import { BarChart3, Users, Wallet, TrendingUp, Zap, Activity, PieChart, ArrowUpRight, ArrowDownRight, ChevronRight, Shield, Clock } from "lucide-react";
import controlRoom from "../assets/control-room.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import EnergyParticles from "../components/EnergyParticles";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const DashboardPreview = () => (
  <div>
    <PageHero
      eyebrow="Dashboard Preview"
      title={<>Your <span className="energy-gradient-text">Investor Dashboard</span></>}
      description="A futuristic, real-time dashboard designed to give you complete visibility into your energy portfolio, earnings, team performance, and rank progression."
      backgroundImage={controlRoom}
    />

    {/* Dashboard Overview Cards */}
    <Section>
      <SectionHeading eyebrow="At a Glance" title="Dashboard Overview" description="Every metric you need, in one unified dark-mode interface. Here's a preview of what partners see when they log in." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { icon: Wallet, label: "Total Earnings", value: "$24,850.60", change: "+12.4%", up: true },
          { icon: BarChart3, label: "Active Packages", value: "3", change: "Surge, Pulse, Blaze", up: true },
          { icon: Users, label: "Team Size", value: "142", change: "+8 this week", up: true },
          { icon: TrendingUp, label: "Current Rank", value: "Wind Leader", change: "Next: Solar Director", up: true },
        ].map((card, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-5 rounded-2xl glass-card glass-card-hover group transition-all duration-500">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <card.icon className="w-5 h-5 text-primary" />
              </div>
              {card.up ? <ArrowUpRight className="w-4 h-4 text-primary" /> : <ArrowDownRight className="w-4 h-4 text-destructive" />}
            </div>
            <div className="font-display text-xl font-bold mb-1">{card.value}</div>
            <div className="text-xs text-muted-foreground">{card.label}</div>
            <div className="text-[10px] text-primary mt-1">{card.change}</div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Binary Tree Preview */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Network View" title="Binary Tree Visualization" description="Track your left and right team growth with an interactive, expandable binary tree." />
        <motion.div {...fadeUp} className="max-w-3xl mx-auto">
          <div className="p-8 rounded-2xl glass-card">
            {/* Tree visualization */}
            <div className="flex flex-col items-center">
              {/* Root */}
              <div className="w-16 h-16 rounded-full energy-gradient flex items-center justify-center mb-2 animate-glow-pulse">
                <span className="font-display text-sm font-bold text-primary-foreground">YOU</span>
              </div>
              <div className="text-xs text-muted-foreground mb-4">Wind Leader • 142 Members</div>
              {/* Branches */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-24 h-px bg-primary/30" />
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <div className="w-24 h-px bg-primary/30" />
              </div>
              <div className="grid grid-cols-2 gap-16 w-full max-w-md">
                {[
                  { side: "Left Team", members: 78, volume: "$185,400", color: "text-primary" },
                  { side: "Right Team", members: 64, volume: "$142,200", color: "text-accent" },
                ].map((team, i) => (
                  <div key={i} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center mx-auto mb-2">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div className="font-display text-xs font-semibold mb-1">{team.side}</div>
                    <div className="text-lg font-bold text-primary">{team.members}</div>
                    <div className="text-[10px] text-muted-foreground">Members</div>
                    <div className="text-xs font-medium mt-1">{team.volume}</div>
                    <div className="text-[10px] text-muted-foreground">Volume</div>
                    {/* Sub nodes */}
                    <div className="flex justify-center gap-6 mt-4">
                      {[1, 2].map((_, j) => (
                        <div key={j} className="w-8 h-8 rounded-full bg-secondary/60 border border-border flex items-center justify-center">
                          <Users className="w-3 h-3 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>

    {/* ROI Graph & Wallet */}
    <Section>
      <SectionHeading eyebrow="Financial Overview" title="ROI & Wallet Snapshot" />
      <div className="grid md:grid-cols-2 gap-8">
        {/* ROI Chart Mock */}
        <motion.div {...fadeUp} className="p-6 rounded-2xl glass-card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-sm font-semibold">Earnings Performance</h3>
            <span className="text-xs text-primary px-2 py-0.5 rounded-full bg-primary/10">Last 30 Days</span>
          </div>
          <div className="h-48 flex items-end gap-1">
            {[35, 42, 38, 55, 48, 62, 58, 70, 65, 78, 72, 85, 80, 92, 88, 95, 90, 98, 94, 100, 96, 105, 100, 110, 106, 115, 110, 120, 116, 125].map((v, i) => (
              <motion.div
                key={i}
                className="flex-1 rounded-t-sm energy-gradient opacity-80"
                initial={{ height: 0 }}
                whileInView={{ height: `${(v / 125) * 100}%` }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03, duration: 0.5 }}
              />
            ))}
          </div>
          <div className="flex justify-between mt-3 text-[10px] text-muted-foreground">
            <span>Day 1</span><span>Day 15</span><span>Day 30</span>
          </div>
        </motion.div>

        {/* Wallet Overview */}
        <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="p-6 rounded-2xl glass-card">
          <h3 className="font-display text-sm font-semibold mb-6">Wallet Overview</h3>
          <div className="space-y-3">
            {[
              { name: "Energy Wallet", balance: "$12,450.00", icon: Zap, pct: 50 },
              { name: "Referral Wallet", balance: "$5,230.40", icon: Users, pct: 21 },
              { name: "Binary Wallet", balance: "$4,120.20", icon: Activity, pct: 17 },
              { name: "Bonus Wallet", balance: "$2,050.00", icon: Shield, pct: 8 },
              { name: "Withdrawal Wallet", balance: "$1,000.00", icon: Wallet, pct: 4 },
            ].map((w, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <w.icon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium">{w.name}</span>
                    <span className="text-xs font-bold">{w.balance}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <motion.div
                      className="h-full energy-gradient rounded-full"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${w.pct}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1, duration: 0.6 }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>

    {/* Rank Progress */}
    <Section className="bg-secondary/20">
      <SectionHeading eyebrow="Your Journey" title="Rank Progression" description="Climb the leadership ranks and unlock exclusive rewards at each tier." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { rank: "Energy Builder", requirement: "$5K Volume", achieved: true },
          { rank: "Wind Leader", requirement: "$25K Volume", achieved: true },
          { rank: "Solar Director", requirement: "$100K Volume", achieved: false },
          { rank: "Global Ambassador", requirement: "$500K Volume", achieved: false },
        ].map((r, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className={`p-5 rounded-2xl text-center transition-all duration-500 ${r.achieved ? "glass-card border-primary/30" : "glass-card opacity-60"}`}>
            <div className={`w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center ${r.achieved ? "energy-gradient animate-glow-pulse" : "bg-muted"}`}>
              <TrendingUp className={`w-6 h-6 ${r.achieved ? "text-primary-foreground" : "text-muted-foreground"}`} />
            </div>
            <div className="font-display text-sm font-bold mb-1">{r.rank}</div>
            <div className="text-[10px] text-muted-foreground">{r.requirement}</div>
            {r.achieved && <div className="text-[10px] text-primary mt-2 font-medium">✓ Achieved</div>}
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Active Package Status */}
    <Section>
      <SectionHeading eyebrow="Package Status" title="Active Energy Packages" />
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { name: "Surge", invested: "$10,000", earned: "$3,200", progress: 32, daysLeft: 204, status: "Active" },
          { name: "Pulse", invested: "$20,000", earned: "$8,800", progress: 44, daysLeft: 196, status: "Active" },
          { name: "Blaze", invested: "$3,000", earned: "$1,470", progress: 49, daysLeft: 128, status: "Active" },
        ].map((pkg, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-5 rounded-2xl glass-card glass-card-hover group transition-all duration-500">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg font-bold">{pkg.name}</div>
              <span className="text-[10px] text-primary px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">{pkg.status}</span>
            </div>
            <div className="space-y-2 text-xs mb-4">
              <div className="flex justify-between"><span className="text-muted-foreground">Invested</span><span className="font-medium">{pkg.invested}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Earned</span><span className="font-medium text-primary">{pkg.earned}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Days Remaining</span><span className="font-medium">{pkg.daysLeft}</span></div>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <motion.div className="h-full energy-gradient rounded-full" initial={{ width: 0 }} whileInView={{ width: `${pkg.progress}%` }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1 text-right">{pkg.progress}% Complete</div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Energy Production Chart */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Energy Data" title="Live Energy Production" description="Monitor real-time power generation across all assets in your portfolio." />
        <motion.div {...fadeUp} className="max-w-4xl mx-auto p-6 rounded-2xl glass-card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full energy-gradient" /><span className="text-xs text-muted-foreground">Wind</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-accent" /><span className="text-xs text-muted-foreground">Solar</span></div>
            </div>
            <div className="flex items-center gap-1 text-xs text-primary"><Clock className="w-3 h-3" /> Live</div>
          </div>
          <div className="h-52 flex items-end gap-[3px]">
            {Array.from({ length: 48 }, (_, i) => {
              const wind = 30 + Math.sin(i * 0.3) * 20 + Math.random() * 15;
              const solar = i > 12 && i < 36 ? 20 + Math.sin((i - 12) * 0.13) * 30 + Math.random() * 10 : Math.random() * 5;
              return (
                <div key={i} className="flex-1 flex flex-col justify-end gap-[1px]">
                  <motion.div className="rounded-t-sm bg-accent/60" initial={{ height: 0 }} whileInView={{ height: `${(solar / 80) * 100}%` }} viewport={{ once: true }} transition={{ delay: i * 0.02, duration: 0.4 }} />
                  <motion.div className="energy-gradient rounded-t-sm opacity-80" initial={{ height: 0 }} whileInView={{ height: `${(wind / 80) * 100}%` }} viewport={{ once: true }} transition={{ delay: i * 0.02, duration: 0.4 }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-3 text-[10px] text-muted-foreground">
            <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
          </div>
        </motion.div>
      </div>
    </Section>
  </div>
);

export default DashboardPreview;
